// "About this piece" — natural customer-facing description composed
// deterministically from known product data (P2.2). Rules:
//   - 1–2 short paragraphs of complete sentences, never key/value dumps.
//   - Only facts present in the product record (metal, finish, feature,
//     stone, closure). No craftsmanship, origin, certification, rarity or
//     quality claims. No supplier/vendor mentions.
//   - Dimensions/weights stay in the Specifications section, not prose.
//   - Too little reliable data -> short description, no filler marketing.

import { specList } from "./specs";
import { displayTitle } from "./title";

const NOUNS = {
  Chains: "chain",
  Rings: "ring",
  Earrings: "pair of earrings",
  Earring: "earring",
  Bracelets: "bracelet",
  Necklaces: "necklace",
  Pendants: "pendant",
  "Pendants & Charms": "pendant",
  Charms: "charm",
  Anklets: "anklet",
  Watches: "watch",
  "Cuff Links": "pair of cuff links",
  "Money Clips": "money clip",
  Sets: "set",
  "Pins & Hair Accessories": "pin",
  "Loose Stones": "stone",
  Stone: "stone",
};

// Known feed value typos normalized to the brand's actual name (factual
// cleanup only — the value clearly denotes Swarovski crystal).
const cleanStone = (v) => {
  const s = String(v).trim().toLowerCase();
  if (/swarovski/.test(s)) return /crystal/.test(s) ? "Swarovski crystals" : "Swarovski crystal";
  return s;
};

const FEATURE_PHRASES = {
  solid: "solid construction",
  "semi-solid": "semi-solid construction",
  hollow: "a hollow construction",
  adjustable: "an adjustable length",
};

const SKIP_VALUES = new Set(["tbd", "n/a", "none", "unknown", ""]);
const usable = (v) => v && !SKIP_VALUES.has(String(v).trim().toLowerCase());

// "14k Tri-Color" + material 14K -> "14K tri-color gold"; "Sterling Silver"
// stays "sterling silver". Only appends a metal noun the data supports.
const metalPhrase = (p) => {
  // N1: nameplate families carry a customer-facing material label resolved
  // from the SKU convention — prefer it over the raw feed Metal_Desc (which
  // uses internal codes like "SS/Gold Plated").
  if (p.np_material) return p.np_material.toLowerCase().replace(/\b(\d+)k\b/g, "$1K");
  if (!p.metal) return null;
  let m = String(p.metal).trim().replace(/\b(\d+)k\b/gi, "$1K");
  if (/^\d+K\s+/.test(m)) {
    m = m.replace(/^(\d+K)\s+(.*)$/, (_, k, rest) => `${k} ${rest.toLowerCase()}`);
    if (!/gold/i.test(m) && ["10K", "14K", "18K+"].includes(p.material)) m = `${m} gold`;
  } else {
    m = m.toLowerCase();
    if (p.material === "Silver" && !/silver/i.test(m)) m = `${m} silver`;
  }
  return m;
};

const aOrAn = (word) => (/^[aeiou]/i.test(word) ? "an" : "a");

export const aboutPiece = (p) => {
  if (!p) return "";
  const specs = specList(p);
  const get = (base) => {
    const hit = specs.find((s) => s.base === base);
    return hit && usable(hit.value) ? hit.value : null;
  };
  const noun = NOUNS[p.category] || "piece";
  const sentences = [];

  // Sentence 1: metal + finish + construction.
  const metal = metalPhrase(p);
  const finish = get("finish");
  const feature = FEATURE_PHRASES[(get("feature") || "").toLowerCase()];
  const extras = [];
  if (finish) extras.push(`a ${finish.toLowerCase()} finish`);
  if (feature) extras.push(feature);
  if (metal && extras.length) {
    sentences.push(`This ${noun} is ${metal} with ${extras.join(" and ")}.`);
  } else if (metal) {
    sentences.push(`This ${noun} is ${metal}.`);
  } else if (extras.length) {
    sentences.push(`This ${noun} has ${extras.join(" and ")}.`);
  }

  // Sentence 2: stone information, when the feed provides it. The first
  // stone takes its shape; additional stone types follow without one.
  const stoneTypes = [
    ...new Set(
      specs
        .filter((s) => s.base === "stone type" && usable(s.value))
        .map((s) => cleanStone(s.value))
    ),
  ].filter(Boolean);
  if (stoneTypes.length) {
    if (p.lab_grown) {
      // Flag beats feed wording: a lab-grown diamond is always described as such.
      for (let i = 0; i < stoneTypes.length; i++) {
        if (/diamond/i.test(stoneTypes[i]) && !/lab-grown/i.test(stoneTypes[i])) {
          stoneTypes[i] = stoneTypes[i].replace(/natural\s*/i, "").replace(/diamond/i, "lab-grown diamond");
        }
      }
    }
    const shape = get("stone shape");
    const first = `${shape ? `${shape.toLowerCase()} ` : ""}${stoneTypes[0]}`;
    const joined = [first, ...stoneTypes.slice(1)].join(" and ");
    const lastWord = first.split(" ").pop();
    const plural = /s$/i.test(lastWord) && !/(ss|us)$/i.test(lastWord);
    sentences.push(`It is set with ${plural ? "" : `${aOrAn(joined)} `}${joined}.`);
  }

  // Sentence 3: closure/clasp, phrased for the jewelry type.
  const clasp = get("clasp /connector") || get("earring closure");
  if (clasp) {
    const c = clasp.toLowerCase();
    const suffix = /clasp|connector|back|post|wire|hook|closure/.test(c) ? "" : " clasp";
    sentences.push(`It fastens with ${aOrAn(c)} ${c}${suffix}.`);
  }

  // Fallback: not enough reliable data — keep it short, just restate the piece.
  if (!sentences.length) sentences.push(`${displayTitle(p)}.`);

  const closer =
    "Questions about this piece? Call or WhatsApp Gold Time Jewelry — family owned since 1991, with showrooms in Katy, Copperas Cove and Killeen.";
  return { main: sentences.join(" "), closer };
};
